/**
 * Shared validation predicates and display helpers for DataTable column cells.
 *
 * Centralised here so the Parrot filler (DataTableField) and the Eagle
 * builder preview (PhonePreviewContent) evaluate emptiness identically.
 */

/** Minimal column shape required by the shared display helpers. */
interface ColumnLike {
  id: string;
  validation?: { required?: boolean } | null;
}

/**
 * Returns true when a cell value should be treated as empty for the purposes
 * of "required" validation.
 *
 * Empty cases:
 *   - undefined / null           (missing)
 *   - ''                         (empty string)
 *   - whitespace-only string     (e.g. '   ')
 *   - empty array                (multi-choice with nothing selected)
 */
export function isCellEmpty(val: unknown): boolean {
  if (val === undefined || val === null) return true;
  if (Array.isArray(val)) return (val as unknown[]).length === 0;
  if (typeof val === 'string') return val.trim() === '';
  return false;
}

/** Returns a display string for a single cell value. Arrays are joined with ', '. */
export function cellDisplay(val: unknown): string {
  if (Array.isArray(val)) return (val as unknown[]).map(String).join(', ');
  return String(val ?? '');
}

/**
 * Returns a title string for a row card.
 *
 * Strategy:
 *   1. First column's display value if not empty (via isCellEmpty).
 *   2. First other column whose value is not empty.
 *   3. `Untitled ${noun}` as final fallback.
 *
 * Whitespace-only values are treated as empty (isCellEmpty handles this).
 */
export function rowCardTitle(
  columns: ColumnLike[],
  row: Record<string, unknown>,
  noun: string,
): string {
  for (const col of columns) {
    if (!isCellEmpty(row[col.id])) return cellDisplay(row[col.id]);
  }
  return `Untitled ${noun}`;
}

/**
 * Returns true when any required column in the row has an empty value.
 */
export function rowHasMissingRequired(
  columns: ColumnLike[],
  row: Record<string, unknown>,
): boolean {
  return columns.some((c) => c.validation?.required && isCellEmpty(row[c.id]));
}
